// The desk conversation: POSTs to /v1/agent/run and reads the server-sent events. A page-tool call
// runs here and its result is sent back, until the agent finishes its answer.
import { apiUrl, toSignIn } from '@/lib/api';
import { runPageTool, type ToolArgs } from './tools';

export type DeskEvent =
  | { type: 'text'; delta: string }
  /** Take this text back off the screen (a garbled reply; the desk asks again). */
  | { type: 'retract'; text: string }
  | { type: 'step'; name: string; args?: ToolArgs }
  /** A card built by code from a tool's result (picks, the market, results…): see desk-cards. */
  | { type: 'component'; card: DeskCardData }
  | { type: 'error'; message: string };

/** A card in the conversation: the tool's own result (`data`), drawn by its kind's renderer. */
export interface DeskCardData { kind: string; tool: string; data: Record<string, unknown> }

type Result = { id: string; name: string; response: Record<string, unknown> };

async function step(conversation: string, body: { message?: string; results?: Result[] }, on: (e: DeskEvent) => void, signal: AbortSignal) {
  const r = await fetch(apiUrl('/v1/agent/run'), {
    method: 'POST', credentials: 'include', cache: 'no-store', signal,
    headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
    body: JSON.stringify({ conversation, ...body }),
  });
  if (r.status === 401) { toSignIn(); return []; }
  if (!r.ok || !r.body) {
    const msg = await r.json().then((j) => j.error as string).catch(() => '');
    on({ type: 'error', message: msg || 'The desk is unavailable right now.' });
    return [];
  }
  const calls: { id: string; name: string; args: ToolArgs }[] = [];
  const reader = r.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let cut;
    while ((cut = buf.indexOf('\n\n')) >= 0) {
      const raw = buf.slice(0, cut); buf = buf.slice(cut + 2);
      let name = 'message', data = '';
      for (const line of raw.split('\n')) {
        if (line.startsWith('event:')) name = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      let d: Record<string, unknown> = {};
      try { d = data ? JSON.parse(data) : {}; } catch { continue; }
      if (name === 'text') on({ type: 'text', delta: String(d.delta ?? '') });
      else if (name === 'retract') on({ type: 'retract', text: String(d.text ?? '') });
      else if (name === 'tool') on({ type: 'step', name: String(d.name) });
      else if (name === 'component') on({ type: 'component', card: d as unknown as DeskCardData });
      else if (name === 'error') on({ type: 'error', message: String(d.message ?? 'Something went wrong') });
      else if (name === 'tool_call') calls.push({ id: String(d.id), name: String(d.name), args: (d.args ?? {}) as ToolArgs });
    }
  }
  return calls;
}

/** Send a message and see it through: run every page tool the agent asks for and pass the results
 * back, until it has answered (at most 8 rounds). */
export async function ask(conversation: string, message: string, on: (e: DeskEvent) => void, signal: AbortSignal) {
  let calls = await step(conversation, { message }, on, signal);
  for (let round = 0; calls.length && round < 8 && !signal.aborted; round++) {
    const results: Result[] = [];
    for (const c of calls) {
      on({ type: 'step', name: c.name, args: c.args });
      results.push({ id: c.id, name: c.name, response: await runPageTool(c.name, c.args) });
    }
    calls = await step(conversation, { results }, on, signal);
  }
}
